import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  fetchAllPlaces, fetchLists,
  adminStats, adminListUsers, adminPatchUser, adminDeleteUser,
  adminDeletePlace, adminDeleteList,
} from "../api";
import { categories } from "../mock";
import AdminPlaceDialog from "../components/AdminPlaceDialog";
import AdminListDialog from "../components/AdminListDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Toaster, toast } from "sonner";
import { Shield, Plus, Pencil, Trash2, ArrowLeft, Search, Loader2, Users as UsersIcon, LogIn, UserPlus, Crown } from "lucide-react";

const catLabel = (id) => categories.find((c) => c.id === id)?.label || id;
const catColor = (id) => categories.find((c) => c.id === id)?.color;

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-xl border border-neutral-200 bg-white p-4">
    <div className="flex items-center gap-2 text-neutral-500">
      <Icon className="h-4 w-4" />
      <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
    </div>
    <div className="mt-2 text-3xl font-extrabold text-neutral-900">{value}</div>
    {sub && <p className="mt-1 text-xs text-neutral-400">{sub}</p>}
  </div>
);

const ConfirmDelete = ({ onConfirm, label }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="ghost" size="sm" className="h-8 px-2 text-red-600 hover:bg-red-50 hover:text-red-700">
        <Trash2 className="h-4 w-4" />
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete {label}?</AlertDialogTitle>
        <AlertDialogDescription>this cannot be undone.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [places, setPlaces] = useState([]);
  const [lists, setLists] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [placeFilter, setPlaceFilter] = useState("");

  const [placeDialog, setPlaceDialog] = useState({ open: false, place: null });
  const [listDialog, setListDialog] = useState({ open: false, list: null });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, l, u, s] = await Promise.all([fetchAllPlaces(), fetchLists(), adminListUsers(), adminStats()]);
      setPlaces(p);
      setLists(l);
      setUsers(u);
      setStats(s);
    } catch (e) {
      toast.error("could not load admin data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading) {
      if (!user || !user.is_admin) {
        navigate("/", { replace: true });
      } else {
        loadAll();
      }
    }
  }, [authLoading, user, navigate, loadAll]);

  const visiblePlaces = useMemo(() => {
    const f = placeFilter.trim().toLowerCase();
    if (!f) return places;
    return places.filter(
      (p) => p.name.toLowerCase().includes(f) || p.area.toLowerCase().includes(f) || p.category.includes(f)
    );
  }, [places, placeFilter]);

  const onDeletePlace = async (id) => {
    try { await adminDeletePlace(id); toast.success("spot deleted."); loadAll(); }
    catch (e) { toast.error("could not delete spot."); }
  };
  const onDeleteList = async (id) => {
    try { await adminDeleteList(id); toast.success("list deleted."); loadAll(); }
    catch (e) { toast.error("could not delete list."); }
  };
  const onDeleteUser = async (id) => {
    try { await adminDeleteUser(id); toast.success("user deleted."); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "could not delete user."); }
  };
  const onToggleUser = async (id, field, value) => {
    try {
      await adminPatchUser(id, { [field]: value });
      setUsers((us) => us.map((u) => (u.id === id ? { ...u, [field]: value } : u)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not update user.");
    }
  };

  if (authLoading || (user && user.is_admin && loading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#faf8f4] text-neutral-400 font-sans">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> loading admin…
      </div>
    );
  }
  if (!user || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-[#faf8f4] font-sans text-neutral-900">
      <Toaster position="top-center" richColors />
      <header className="flex items-center gap-3 border-b border-neutral-200 bg-white px-4 md:px-8 py-3">
        <Link to="/" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800">
          <ArrowLeft className="h-4 w-4" /> back
        </Link>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-neutral-800" />
          <h1 className="text-lg font-bold">Admin control</h1>
        </div>
        <span className="ml-auto text-sm text-neutral-500">{user.email}</span>
      </header>

      <main className="mx-auto max-w-6xl px-4 md:px-8 py-6">
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="spots">Spots ({places.length})</TabsTrigger>
            <TabsTrigger value="lists">Lists ({lists.length})</TabsTrigger>
            <TabsTrigger value="users">Users ({users.length})</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <StatCard icon={UsersIcon} label="Total users" value={stats?.total_users ?? 0}
                sub={`${stats?.premium_users ?? 0} premium · ${stats?.google_users ?? 0} via Google`} />
              <StatCard icon={UserPlus} label="Total signups" value={stats?.total_signups ?? 0}
                sub={`${stats?.signups_today ?? 0} today · ${stats?.signups_week ?? 0} this week`} />
              <StatCard icon={LogIn} label="Total logins" value={stats?.total_logins ?? 0}
                sub={`${stats?.logins_today ?? 0} today · ${stats?.logins_week ?? 0} this week`} />
              <StatCard icon={Crown} label="Premium members" value={stats?.premium_users ?? 0}
                sub="unlocked submit + lists" />
            </div>

            <div className="mt-5 overflow-hidden rounded-xl border border-neutral-200 bg-white">
              <div className="border-b border-neutral-100 px-4 py-3 text-sm font-semibold text-neutral-700">
                Recent activity
              </div>
              {stats?.recent?.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-28">Event</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="w-24">Via</TableHead>
                      <TableHead className="w-44">When</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.recent.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${e.type === "signup" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>
                            {e.type}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{e.email}</TableCell>
                        <TableCell className="text-sm text-neutral-500">{e.provider}</TableCell>
                        <TableCell className="text-sm text-neutral-500">
                          {e.at ? new Date(e.at).toLocaleString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="px-4 py-8 text-center text-sm text-neutral-400">no activity yet.</p>
              )}
            </div>
          </TabsContent>

          {/* SPOTS */}
          <TabsContent value="spots" className="mt-4">
            <div className="mb-3 flex items-center gap-2">
              <div className="relative max-w-xs flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <Input value={placeFilter} onChange={(e) => setPlaceFilter(e.target.value)} placeholder="filter spots…" className="h-9 pl-9 text-sm" />
              </div>
              <Button onClick={() => setPlaceDialog({ open: true, place: null })} className="ml-auto h-9 rounded-full bg-neutral-900 text-white hover:bg-neutral-800">
                <Plus className="mr-1 h-4 w-4" /> Add spot
              </Button>
            </div>
            <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Area</TableHead>
                    <TableHead className="w-16">Rating</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visiblePlaces.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">
                        {p.name}
                        {p.community && <span className="ml-2 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">community</span>}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 text-sm text-neutral-600">
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: catColor(p.category) }} />
                          {catLabel(p.category)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-neutral-600">{p.area}</TableCell>
                      <TableCell className="text-sm">{p.rating ?? "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setPlaceDialog({ open: true, place: p })}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <ConfirmDelete label={p.name} onConfirm={() => onDeletePlace(p.id)} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* LISTS */}
          <TabsContent value="lists" className="mt-4">
            <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>List</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead className="w-20">Spots</TableHead>
                    <TableHead className="w-24">Type</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lists.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="font-medium">{l.label}</TableCell>
                      <TableCell className="text-sm text-neutral-600">{l.author}</TableCell>
                      <TableCell className="text-sm">{l.count}</TableCell>
                      <TableCell>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${l.is_community ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-600"}`}>
                          {l.is_community ? "community" : "curated"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setListDialog({ open: true, list: l })}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <ConfirmDelete label={l.label} onConfirm={() => onDeleteList(l.id)} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users" className="mt-4">
            <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead className="w-24">Provider</TableHead>
                    <TableHead className="w-28">Premium</TableHead>
                    <TableHead className="w-28">Admin</TableHead>
                    <TableHead className="w-20 text-right">Delete</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const isSelf = u.id === user.id;
                    return (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">
                          {u.email}
                          {isSelf && <span className="ml-2 text-xs text-neutral-400">(you)</span>}
                        </TableCell>
                        <TableCell className="text-sm text-neutral-500">{u.provider || "email"}</TableCell>
                        <TableCell>
                          <Switch checked={u.is_premium} onCheckedChange={(v) => onToggleUser(u.id, "is_premium", v)} />
                        </TableCell>
                        <TableCell>
                          <Switch checked={u.is_admin} disabled={isSelf} onCheckedChange={(v) => onToggleUser(u.id, "is_admin", v)} />
                        </TableCell>
                        <TableCell className="text-right">
                          {!isSelf && <ConfirmDelete label={u.email} onConfirm={() => onDeleteUser(u.id)} />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <AdminPlaceDialog
        open={placeDialog.open}
        onOpenChange={(v) => setPlaceDialog((s) => ({ ...s, open: v }))}
        place={placeDialog.place}
        onSaved={loadAll}
      />
      <AdminListDialog
        open={listDialog.open}
        onOpenChange={(v) => setListDialog((s) => ({ ...s, open: v }))}
        list={listDialog.list}
        allPlaces={places}
        onSaved={loadAll}
      />
    </div>
  );
}

export default Admin;
